/**
 * L.7 / L.8 / L.9 — Specialization Lab window.
 *
 * Side-by-side comparison of base-model output vs LoRA-overlay output
 * for the same prompt. Designed to make "what does specialization
 * actually do" visceral — the user sees both columns generate
 * (faked-streaming for L.8 since the trainer returns one blob),
 * a "what changed" card highlighting heuristic deltas, and live
 * levers (LoRA strength, temperature, edit prompt + re-run).
 *
 * Sources of context (L.7):
 *   - Latest complete `tinkerSpikeRuns` row via the existing
 *     `lorasByPortal.findBySourceRun` lookup.
 *   - Marketplace `probePrompts` (L.2) keyed by the skill name(s) used.
 *
 * Lifecycle:
 *   1. On mount, the active LoRA + first probe prompt are pulled.
 *   2. "Run both" fires two `inferenceJobs.request` mutations
 *      against the same prompt — one without `loraUrl`, one with.
 *   3. Each mutation result id is polled via `inferenceJobs.get`
 *      until status === 'complete' or 'failed'.
 *   4. The "complete" text is fake-streamed to the column (~3s) so
 *      the divergence feels live.
 *   5. The "what changed" card recomputes heuristics from both blobs.
 *
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useNipeControl } from '../nipe/NipeControlContext';
import { useWindowContext } from '../desktop/WindowContext';
import { computeWhatChanged } from './whatChanged';
import { HelpTip } from '../ui/HelpTip';
import { consumeLabHandoff, type LabHandoff } from './labHandoff';
import {
  SpecializationSettingsDrawer,
  autoLearningRateFor,
  describeControlsFromSettings,
  type RespecializeStatus,
} from './SpecializationSettingsDrawer';
import type {
  LabDatasetScope,
  LabHyperparameters,
  SpecializationLabBridge,
} from './types';

const DEFAULT_LAB_SETTINGS: LabHyperparameters = {
  baseModel: 'meta-llama/Llama-3.2-1B',
  loraRank: 8,
  epochs: 1,
  learningRate: 2e-4,
  learningRatePreset: 'manual',
  costCapUsd: 5,
  datasetScope: ['firm-skills'],
};

const FALLBACK_PROBE_PROMPT =
  "Draft a one-paragraph legal motion in your firm's house style.";

type ColumnState =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'queued'; jobId: string }
  | { kind: 'streaming'; jobId: string; rendered: string; full: string }
  | { kind: 'complete'; jobId: string; text: string; ms?: number }
  | { kind: 'error'; message: string };

const STREAM_DURATION_MS = 3000;
const STREAM_TICK_MS = 40;

function useFakeStream(
  fullText: string | null,
  active: boolean,
  onTick: (rendered: string) => void,
  onComplete: () => void,
): void {
  const ranRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || fullText === null) return;
    if (ranRef.current === fullText) return;
    ranRef.current = fullText;
    const total = fullText.length;
    if (total === 0) {
      onComplete();
      return;
    }
    const start = Date.now();
    const tick = (): void => {
      const elapsed = Date.now() - start;
      const ratio = Math.min(1, elapsed / STREAM_DURATION_MS);
      const chars = Math.floor(total * ratio);
      onTick(fullText.slice(0, chars));
      if (ratio >= 1) {
        onComplete();
        return;
      }
      window.setTimeout(tick, STREAM_TICK_MS);
    };
    tick();
  }, [fullText, active, onTick, onComplete]);
}

interface ColumnProps {
  title: string;
  testIdRoot: string;
  testIdOutput: string;
  testIdStatus: string;
  state: ColumnState;
  /** Rendered in the idle empty-state slot. Lets the parent explain
   * *why* the column is empty (no trained LoRA vs. ready-to-run) and
   * point the user at the next concrete action. */
  emptyHint: ReactNode;
}

function Column({
  title,
  testIdRoot,
  testIdOutput,
  testIdStatus,
  state,
  emptyHint,
}: ColumnProps): ReactElement {
  const rendered =
    state.kind === 'streaming'
      ? state.rendered
      : state.kind === 'complete'
        ? state.text
        : '';
  const statusLine =
    state.kind === 'idle'
      ? 'Idle'
      : state.kind === 'requesting'
        ? 'Requesting…'
        : state.kind === 'queued'
          ? `Queued (${state.jobId.slice(0, 6)}…)`
          : state.kind === 'streaming'
            ? 'Streaming…'
            : state.kind === 'complete'
              ? `Complete${state.ms ? ` · ${(state.ms / 1000).toFixed(1)}s` : ''}`
              : `Error: ${state.message}`;
  return (
    <div
      data-testid={testIdRoot}
      className="flex-1 flex flex-col border border-solid border-[#808080] bg-white text-black"
    >
      <div className="flex items-center justify-between px-2 py-1 bg-[#ece9d8] border-b border-solid border-b-[#808080]">
        <span className="font-bold text-[11px]">{title}</span>
        <span data-testid={testIdStatus} className="text-[10px] text-gray-800">
          {statusLine}
        </span>
      </div>
      <div
        data-testid={testIdOutput}
        className="flex-1 overflow-auto p-2 text-[11px] whitespace-pre-wrap break-words font-[Consolas,Menlo,monospace] leading-relaxed"
      >
        {rendered
          ? rendered
          : state.kind === 'idle'
            ? <div className="text-gray-800 italic font-sans text-[11px] leading-snug">{emptyHint}</div>
            : null}
      </div>
    </div>
  );
}

interface CorpusDrawerProps {
  skillNames: string[];
  baseModel: string | null;
  loraRank: number | null;
  trainedAt: number | null;
  onChangeSpecialization: () => void;
}

function CorpusDrawer({
  skillNames,
  baseModel,
  loraRank,
  trainedAt,
  onChangeSpecialization,
}: CorpusDrawerProps): ReactElement {
  return (
    <aside
      data-testid={testIds.specializationLab.corpusDrawer}
      // Mobile: drop the fixed width + side border, switch to a top border
      // so the drawer reads as "below" the main area in the stacked layout.
      // md+: original 220px-wide left-bordered sidebar.
      className="w-[220px] border-l border-solid border-l-[#808080] bg-[#ece9d8] p-2 text-[11px] text-black flex flex-col gap-2 max-md:w-full max-md:border-l-0 max-md:border-t max-md:border-t-[#808080]"
    >
      <div className="font-bold text-[11px]">Studied</div>
      {skillNames.length === 0 ? (
        <div className="text-[11px] text-black leading-snug">
          <div className="font-bold mb-1">Nothing trained yet</div>
          <div className="italic text-gray-800">
            A specialization is a LoRA adapter — a small overlay that
            teaches the base model your firm's house style for one Skill.
          </div>
          <div className="italic text-gray-800 mt-1">
            Open the Skill Library, pick a Skill, and click{' '}
            <em>"Specialize the model on this skill."</em> When training
            finishes, the Studied panel will list it here.
          </div>
        </div>
      ) : (
        <ul className="list-disc list-inside space-y-0.5">
          {skillNames.map((n) => (
            <li key={n} className="break-words">
              📄 {n}
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-solid border-t-[#aca899] pt-1 mt-1 space-y-0.5">
        {baseModel && (
          <div>
            <span className="font-bold">Base:</span> {baseModel}
          </div>
        )}
        {loraRank !== null && (
          <div>
            <span className="font-bold">Rank:</span> {loraRank}
          </div>
        )}
        {trainedAt && (
          <div>
            <span className="font-bold">Trained:</span>{' '}
            {new Date(trainedAt).toLocaleDateString()}
          </div>
        )}
      </div>
      <button
        type="button"
        data-testid={testIds.specializationLab.changeSpecializationLink}
        onClick={onChangeSpecialization}
        className="mt-1 text-[11px] text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0 text-left"
        title="Open the Skill Library to pick a different skill (or bundle) to specialize on."
      >
        ↻ Specialize a different skill →
      </button>
    </aside>
  );
}

export function SpecializationLabWindow(): ReactElement {
  const nipe = useNipeControl();
  const requestInference = useMutation(api.inferenceJobs.request);
  const kickSampler = useAction(api.inferenceJobs.kickSampler);

  // Pull the latest complete training run from the Tinker side; that's
  // the source of provenance + the LoRA URL.
  const recentTinkerRuns = useQuery(
    api.tinkerSpikeRuns.listRecent,
    { limit: 1 },
  ) as
    | Array<{ _id: string; status: string; skillNamesUsedJson?: string; completedAt?: number }>
    | undefined;
  const latestRun = recentTinkerRuns?.find((r) => r.status === 'complete') ?? null;

  const skillNames = useMemo<string[]>(() => {
    if (!latestRun?.skillNamesUsedJson) return [];
    try {
      const parsed = JSON.parse(latestRun.skillNamesUsedJson);
      return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === 'string') : [];
    } catch {
      return [];
    }
  }, [latestRun?.skillNamesUsedJson]);

  const registeredLora = useQuery(
    api.lorasByPortal.findBySourceRun,
    latestRun?._id ? { sourceRunRowId: latestRun._id } : 'skip',
  ) as { tinkerLoraUrl: string; baseModel: string; loraRank: number } | null | undefined;

  // L.2 / L.16 — pull the marketplace catalog so we can offer (a) a
  // skill picker dropdown and (b) a probe selector that cycles through
  // `probePrompts[]` for the currently-selected skill. Falls back to a
  // generic prompt when no curated probes are registered.
  const marketplaceForFirstSkill = useQuery(
    api.skillMarketplace.listMarketplace,
    {},
  ) as
    | Array<{ name: string; probePrompts?: string[] }>
    | undefined;

  // F18.G.3 — Send-to-Lab handshake. If a sender (TrainLoraButton,
  // BundleSpecializeButton, NIPE openSpecializationLab tool) wrote a
  // `__repancy_lab_pending` localStorage record within the last 8s,
  // consume it ONCE on mount and use it to seed selectedSkill +
  // prompt. consumeLabHandoff clears the record so subsequent mounts
  // don't re-apply a stale seed.
  const handoffRef = useRef<LabHandoff | null>(null);
  if (handoffRef.current === null) {
    handoffRef.current = consumeLabHandoff();
  }
  const handoff = handoffRef.current;

  // L.16 — Selected skill defaults to the handoff payload first; then
  // the first one from the latest training run; falls back to the
  // first marketplace skill so the dropdown's visible selection
  // always matches probe state. User can switch via the header
  // dropdown.
  const [selectedSkill, setSelectedSkill] = useState<string | null>(
    handoff?.skillName ?? null,
  );
  useEffect(() => {
    if (selectedSkill !== null) return;
    if (skillNames.length > 0) {
      setSelectedSkill(skillNames[0] ?? null);
      return;
    }
    if (marketplaceForFirstSkill && marketplaceForFirstSkill.length > 0) {
      setSelectedSkill(marketplaceForFirstSkill[0]?.name ?? null);
    }
  }, [skillNames, selectedSkill, marketplaceForFirstSkill]);

  const probesForSelected = useMemo<string[]>(() => {
    if (!selectedSkill) return [];
    const row = marketplaceForFirstSkill?.find((m) => m.name === selectedSkill);
    return row?.probePrompts ?? [];
  }, [selectedSkill, marketplaceForFirstSkill]);

  const [probeIndex, setProbeIndex] = useState<number>(0);
  // When the user switches skill or probes load in late, snap probe index
  // back to 0 and seed the prompt textarea with the curated probe.
  const lastSkillRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastSkillRef.current === selectedSkill) return;
    lastSkillRef.current = selectedSkill;
    setProbeIndex(0);
  }, [selectedSkill]);

  const initialProbe = useMemo<string>(() => {
    return probesForSelected[probeIndex] ?? FALLBACK_PROBE_PROMPT;
  }, [probesForSelected, probeIndex]);

  // F18.G.3 — If the handoff supplied probeText, treat it as
  // user-owned from the first render: initial prompt = handoff text,
  // and lastSeededProbeRef is set to a sentinel that won't match
  // anything the curated-probe effect produces, so the effect's
  // "current === lastSeededProbeRef.current" branch stays false and
  // the user's text survives.
  const HANDOFF_SENTINEL = '\0__handoff__\0';
  const [prompt, setPrompt] = useState<string>(
    handoff?.probeText ?? FALLBACK_PROBE_PROMPT,
  );
  const lastSeededProbeRef = useRef<string>(
    handoff?.probeText !== undefined ? HANDOFF_SENTINEL : FALLBACK_PROBE_PROMPT,
  );
  useEffect(() => {
    // Replace the prompt text when the curated probe changes — unless
    // the user has typed something custom that doesn't match the last
    // seeded probe (treat that as "user owns the prompt now").
    setPrompt((current) => {
      if (current === lastSeededProbeRef.current || current === FALLBACK_PROBE_PROMPT) {
        lastSeededProbeRef.current = initialProbe;
        return initialProbe;
      }
      return current;
    });
  }, [initialProbe]);

  const cycleProbe = useCallback(
    (delta: number): void => {
      if (probesForSelected.length === 0) return;
      const next =
        (probeIndex + delta + probesForSelected.length) %
        probesForSelected.length;
      setProbeIndex(next);
    },
    [probeIndex, probesForSelected.length],
  );

  const marketplaceSkillNames = useMemo<string[]>(() => {
    return (marketplaceForFirstSkill ?? []).map((m) => m.name);
  }, [marketplaceForFirstSkill]);

  const [probeHelpOpen, setProbeHelpOpen] = useState<boolean>(false);

  const { openWindow } = useWindowContext();

  const [temperature, setTemperature] = useState<number>(0.7);
  const [loraStrength, setLoraStrength] = useState<number>(1.0);

  const [baseState, setBaseState] = useState<ColumnState>({ kind: 'idle' });
  const [loraState, setLoraState] = useState<ColumnState>({ kind: 'idle' });

  // L.15 — Specialization settings drawer state.
  // Seeded from `tinkerTrainingSettings.current` (operator-grade
  // defaults) once the query resolves; until then, falls back to
  // DEFAULT_LAB_SETTINGS so the drawer renders deterministically.
  const persistedSettings = useQuery(
    api.tinkerTrainingSettings.current,
    {},
  ) as
    | {
        baseModel: string;
        loraRank: number;
        epochs: number;
        learningRate: number;
        costCapUsd: number;
        datasetScopeJson: string;
      }
    | null
    | undefined;
  const [labSettings, setLabSettings] = useState<LabHyperparameters>(
    DEFAULT_LAB_SETTINGS,
  );
  const settingsSeededRef = useRef<boolean>(false);
  useEffect(() => {
    if (settingsSeededRef.current) return;
    if (persistedSettings === undefined) return; // still loading
    settingsSeededRef.current = true;
    if (persistedSettings === null) return; // no row yet; keep defaults
    let scope: LabDatasetScope[] = ['firm-skills'];
    try {
      const parsed = JSON.parse(persistedSettings.datasetScopeJson);
      if (Array.isArray(parsed)) {
        scope = parsed.filter(
          (k): k is LabDatasetScope =>
            k === 'firm-skills' || k === 'vault-notes' || k === 'audit-log',
        );
        if (scope.length === 0) scope = ['firm-skills'];
      }
    } catch {
      // ignore; keep default
    }
    setLabSettings({
      baseModel: persistedSettings.baseModel,
      loraRank: persistedSettings.loraRank,
      epochs: persistedSettings.epochs,
      learningRate: persistedSettings.learningRate,
      learningRatePreset: 'manual',
      costCapUsd: persistedSettings.costCapUsd,
      datasetScope: scope,
    });
  }, [persistedSettings]);

  // L.15.3 — Re-specialize lifecycle.
  const requestTrainingJob = useMutation(api.tinkerTrainingJobs.request);
  const kickTrainingWorker = useAction(api.tinkerTrainingJobs.kickWorker);
  const [respecializeStatus, setRespecializeStatus] =
    useState<RespecializeStatus>({ kind: 'idle' });

  const handleRespecialize = useCallback(async (): Promise<void> => {
    setRespecializeStatus({ kind: 'requesting', message: 'Enqueueing…' });
    try {
      // Snapshot the dataset scope. If we have a currently-loaded LoRA
      // with a known skill bundle, default to specializing on the same
      // bundle; otherwise honor whatever's checked in the drawer.
      const datasetScopeJson =
        skillNames.length > 0
          ? JSON.stringify([`skill-bundle:${JSON.stringify(skillNames)}`])
          : JSON.stringify(labSettings.datasetScope);
      const effectiveLearningRate =
        labSettings.learningRatePreset === 'auto-cookbook'
          ? autoLearningRateFor(labSettings.baseModel)
          : labSettings.learningRate;
      const jobId = await requestTrainingJob({
        baseModel: labSettings.baseModel,
        loraRank: labSettings.loraRank,
        epochs: labSettings.epochs,
        learningRate: effectiveLearningRate,
        costCapUsd: labSettings.costCapUsd,
        datasetScopeJson,
      });
      setRespecializeStatus({ kind: 'kicking', message: 'Kicking worker…' });
      const result = await kickTrainingWorker({
        jobId,
      });
      if (!result.ok) {
        setRespecializeStatus({
          kind: 'error',
          message: result.reason ?? 'Worker unreachable. See logs for details.',
        });
        return;
      }
      setRespecializeStatus({ kind: 'queued', message: 'queued' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRespecializeStatus({ kind: 'error', message });
    }
  }, [
    labSettings,
    skillNames,
    requestTrainingJob,
    kickTrainingWorker,
  ]);

  // Poll active jobs every 4s while queued or claimed.
  const baseJobId = baseState.kind === 'queued' || baseState.kind === 'streaming' ? (baseState as { jobId: string }).jobId : null;
  const loraJobId = loraState.kind === 'queued' || loraState.kind === 'streaming' ? (loraState as { jobId: string }).jobId : null;
  const basePoll = useQuery(
    api.inferenceJobs.get,
    baseJobId ? { jobId: baseJobId } : 'skip',
  ) as { status: string; text?: string; ms?: number; errorMessage?: string } | null | undefined;
  const loraPoll = useQuery(
    api.inferenceJobs.get,
    loraJobId ? { jobId: loraJobId } : 'skip',
  ) as { status: string; text?: string; ms?: number; errorMessage?: string } | null | undefined;

  // Transition queued → streaming when poll returns complete.
  useEffect(() => {
    if (baseState.kind !== 'queued') return;
    if (!basePoll) return;
    if (basePoll.status === 'complete' && basePoll.text) {
      setBaseState({ kind: 'streaming', jobId: baseState.jobId, rendered: '', full: basePoll.text });
    } else if (basePoll.status === 'failed') {
      setBaseState({ kind: 'error', message: basePoll.errorMessage ?? 'Inference failed' });
    }
  }, [basePoll, baseState]);

  useEffect(() => {
    if (loraState.kind !== 'queued') return;
    if (!loraPoll) return;
    if (loraPoll.status === 'complete' && loraPoll.text) {
      setLoraState({ kind: 'streaming', jobId: loraState.jobId, rendered: '', full: loraPoll.text });
    } else if (loraPoll.status === 'failed') {
      setLoraState({ kind: 'error', message: loraPoll.errorMessage ?? 'Inference failed' });
    }
  }, [loraPoll, loraState]);

  // L.8 — fake-stream the completed text into the column over ~3s.
  useFakeStream(
    baseState.kind === 'streaming' ? baseState.full : null,
    baseState.kind === 'streaming',
    useCallback(
      (rendered) => {
        setBaseState((prev) =>
          prev.kind === 'streaming' ? { ...prev, rendered } : prev,
        );
      },
      [],
    ),
    useCallback(() => {
      setBaseState((prev) => {
        if (prev.kind !== 'streaming') return prev;
        const ms = basePoll?.ms;
        return { kind: 'complete', jobId: prev.jobId, text: prev.full, ms };
      });
    }, [basePoll?.ms]),
  );
  useFakeStream(
    loraState.kind === 'streaming' ? loraState.full : null,
    loraState.kind === 'streaming',
    useCallback(
      (rendered) => {
        setLoraState((prev) =>
          prev.kind === 'streaming' ? { ...prev, rendered } : prev,
        );
      },
      [],
    ),
    useCallback(() => {
      setLoraState((prev) => {
        if (prev.kind !== 'streaming') return prev;
        const ms = loraPoll?.ms;
        return { kind: 'complete', jobId: prev.jobId, text: prev.full, ms };
      });
    }, [loraPoll?.ms]),
  );

  // L.9 — Run Both kicks two parallel inferenceJobs.request calls.
  // Both share the same prompt + temperature; LoRA gets the URL.
  const baseModel = registeredLora?.baseModel ?? 'meta-llama/Llama-3.2-1B';
  const loraUrl = registeredLora?.tinkerLoraUrl ?? null;
  const canRun = prompt.trim().length > 0 && loraUrl !== null;
  const runBoth = useCallback(async (): Promise<void> => {
    if (!canRun || loraUrl === null) return;
    setBaseState({ kind: 'requesting' });
    setLoraState({ kind: 'requesting' });
    try {
      const [baseJob, loraJob] = await Promise.all([
        requestInference({
          prompt,
          baseModel,
          temperature,
        }),
        requestInference({
          prompt,
          baseModel,
          loraUrl,
          temperature,
        }),
      ]);
      setBaseState({ kind: 'queued', jobId: baseJob });
      setLoraState({ kind: 'queued', jobId: loraJob });
      // Kick the sampler ACA Job for each side. We don't block the UI on
      // the worker result — the poll loops above transition the column
      // state when the row flips to complete.
      void kickSampler({
        inferenceJobId: baseJob,
      });
      void kickSampler({
        inferenceJobId: loraJob,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setBaseState({ kind: 'error', message });
      setLoraState({ kind: 'error', message });
    }
  }, [canRun, loraUrl, prompt, baseModel, temperature, requestInference, kickSampler]);

  const bothComplete = baseState.kind === 'complete' && loraState.kind === 'complete';
  const whatChanged = useMemo(() => {
    if (!bothComplete) return null;
    return computeWhatChanged(
      (baseState as { text: string }).text,
      (loraState as { text: string }).text,
    );
  }, [bothComplete, baseState, loraState]);

  // L.15.4 — Register the read-only `window.__repancySpecializationLab`
  // bridge so NIPE tools can introspect the Lab. NO setters per D4 —
  // NIPE describes controls, the user owns the sliders.
  useEffect(() => {
    const bridge: SpecializationLabBridge = {
      currentSettings: () => labSettings,
      describeControls: () => describeControlsFromSettings(labSettings),
      isOpen: () => true,
    };
    window.__repancySpecializationLab = bridge;
    return () => {
      delete window.__repancySpecializationLab;
    };
  }, [labSettings]);

  return (
    <div
      data-testid={testIds.specializationLab.root}
      className="w-full h-full flex flex-col bg-[#ece9d8] text-black font-[MS_Sans_Serif,Tahoma,Geneva,sans-serif]"
    >
      <header
        data-testid={testIds.specializationLab.header}
        className="px-2 py-1.5 border-b-2 border-solid border-b-[#808080] flex items-center gap-3 bg-[#f1efe2] max-md:flex-wrap max-md:gap-y-1"
      >
        <span className="font-bold text-[12px]">🧠 Specialization Lab</span>
        <span className="text-[10px] text-gray-800">·</span>
        <label className="flex items-center gap-1 text-[10px] text-black">
          <span className="font-bold">Specialization</span>
          <select
            data-testid={testIds.specializationLab.skillPicker}
            value={selectedSkill ?? ''}
            onChange={(e) => setSelectedSkill(e.target.value || null)}
            disabled={marketplaceSkillNames.length === 0}
            className="text-[11px] px-1 py-0.5 border border-solid border-[#808080] bg-white text-black max-w-[260px]"
            title="Pick which skill's specialization to compare against the base model."
          >
            {marketplaceSkillNames.length === 0 ? (
              <option value="">(no skills in marketplace yet)</option>
            ) : (
              <>
                {selectedSkill && !marketplaceSkillNames.includes(selectedSkill) && (
                  <option value={selectedSkill}>{selectedSkill}</option>
                )}
                {marketplaceSkillNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                    {skillNames.includes(n) ? '  ✓ trained' : ''}
                  </option>
                ))}
              </>
            )}
          </select>
        </label>
        {latestRun?.completedAt && (
          <span className="text-[10px] text-gray-700 ml-auto">
            Latest trained {new Date(latestRun.completedAt).toLocaleDateString()}
          </span>
        )}
      </header>

      {/* Mobile: stack main + corpus drawer vertically so the drawer
          doesn't eat 220px of a 320px viewport. md+ keeps the original
          side-by-side flex row. overflow-auto on the wrapper lets the
          stacked layout scroll vertically on mobile. */}
      <div className="flex flex-1 overflow-hidden max-md:flex-col max-md:overflow-auto">
        <main className="flex flex-1 flex-col p-3 gap-3 overflow-auto max-md:overflow-visible">
          {/* Probe groupbox */}
          <fieldset
            data-testid={testIds.specializationLab.probeGroup}
            className="border border-solid border-[#7f9db9] bg-white px-3 pt-1 pb-2"
          >
            <legend className="px-1 text-[11px] font-bold text-black flex items-center gap-1">
              <span>Probe prompt</span>
              <button
                type="button"
                data-help-btn
                data-testid={testIds.specializationLab.probeHelpToggle}
                onClick={() => setProbeHelpOpen((v) => !v)}
                aria-label="What is a probe prompt?"
                aria-expanded={probeHelpOpen}
              >
                ?
              </button>
            </legend>

            {probeHelpOpen && (
              <div
                data-testid={testIds.specializationLab.probeHelpPanel}
                className="mb-2 border border-solid border-[#7f9db9] bg-[#fffbcc] p-2 text-[11px] text-black"
              >
                <p>
                  A <strong>probe</strong> is a prompt designed to surface
                  the difference between the base model and your
                  specialized one. Each Skill ships with a curated set of
                  probes — short tasks the Skill is supposed to be good
                  at — so you can see at a glance whether the
                  specialization actually moved the needle.
                </p>
                <p className="mt-1">
                  Cycle through the curated probes for this skill with the
                  arrows, or type your own. <em>"Run both"</em> sends the
                  same probe to both columns: base on the left,
                  specialized on the right.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 mb-1">
              <label className="text-[10px] text-gray-800">
                Curated probes for this skill:
              </label>
              {probesForSelected.length > 0 ? (
                <>
                  <button
                    type="button"
                    data-testid={testIds.specializationLab.probePrev}
                    onClick={() => cycleProbe(-1)}
                    disabled={probesForSelected.length <= 1}
                    className="text-[11px] px-1.5 py-0.5 border border-solid border-[#808080] bg-[#ece9d8] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous probe"
                  >
                    ◀
                  </button>
                  <span
                    data-testid={testIds.specializationLab.probeIndex}
                    className="text-[10px] text-black"
                  >
                    Probe {probeIndex + 1} of {probesForSelected.length}
                  </span>
                  <button
                    type="button"
                    data-testid={testIds.specializationLab.probeNext}
                    onClick={() => cycleProbe(1)}
                    disabled={probesForSelected.length <= 1}
                    className="text-[11px] px-1.5 py-0.5 border border-solid border-[#808080] bg-[#ece9d8] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next probe"
                  >
                    ▶
                  </button>
                  {probesForSelected.length > 1 && (
                    <select
                      data-testid={testIds.specializationLab.probeSelect}
                      value={probeIndex}
                      onChange={(e) => setProbeIndex(Number(e.target.value))}
                      className="text-[10px] px-1 py-0.5 border border-solid border-[#808080] bg-white text-black max-w-[280px]"
                    >
                      {probesForSelected.map((p, i) => (
                        <option key={i} value={i}>
                          {`#${i + 1} — ${p.slice(0, 48)}${p.length > 48 ? '…' : ''}`}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              ) : (
                <span className="text-[10px] italic text-gray-700">
                  {selectedSkill
                    ? 'No curated probes registered for this skill — type your own below.'
                    : 'Pick a skill above to load its curated probes.'}
                </span>
              )}
            </div>

            <textarea
              data-testid={testIds.specializationLab.promptInput}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                lastSeededProbeRef.current = e.target.value;
              }}
              rows={4}
              className="w-full text-[12px] p-2 border border-solid border-[#808080] bg-white text-black font-[Consolas,Menlo,monospace] resize-y"
            />

            <div className="flex items-center justify-end mt-2 gap-2">
              {!canRun && loraUrl === null && (
                <span className="text-[11px] italic text-gray-800">
                  Nothing to compare against yet —{' '}
                  <button
                    type="button"
                    onClick={() => openWindow('/skills')}
                    className="text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0 italic"
                  >
                    open the Skill Library
                  </button>{' '}
                  and train a specialization first.
                </span>
              )}
              <button
                type="button"
                data-testid={testIds.specializationLab.runBothButton}
                onClick={() => void runBoth()}
                disabled={!canRun || baseState.kind === 'requesting' || baseState.kind === 'queued' || baseState.kind === 'streaming'}
                className="text-[12px] px-4 py-1 cursor-pointer font-bold disabled:opacity-50"
                style={{
                  background: 'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
                  color: '#000',
                  border: '1px solid #003c74',
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                }}
                title={
                  loraUrl === null
                    ? 'No specialization registered yet. Specialize a skill from the Library first.'
                    : 'Run both base + specialized on the same prompt'
                }
              >
                ↻ Run both
              </button>
            </div>
          </fieldset>

          {/* Settings drawer — now expanded by default */}
          <SpecializationSettingsDrawer
            settings={labSettings}
            onChange={setLabSettings}
            corpusSize={skillNames.length}
            respecializeStatus={respecializeStatus}
            onRespecialize={() => void handleRespecialize()}
          />

          {/* Sampling levers groupbox */}
          <fieldset
            data-testid={testIds.specializationLab.leversGroup}
            className="border border-solid border-[#7f9db9] bg-white px-3 pt-1 pb-2"
          >
            <legend className="px-1 text-[11px] font-bold text-black">
              Sampling levers
            </legend>
            <div className="flex gap-6 items-center text-[11px] text-black max-md:flex-wrap max-md:gap-y-2 max-md:gap-x-4">
              <HelpTip
                short="Temperature — how adventurous the model is when picking each next word."
                long={
                  <>
                    <p>
                      Controls how adventurous the model is when picking
                      each next word.
                    </p>
                    <p className="mt-1">
                      <strong>Higher</strong> → more variety, more
                      surprise, occasionally off-topic.
                      <br />
                      <strong>Lower</strong> → tighter, more deterministic
                      answers; near 0 the model repeats itself.
                    </p>
                  </>
                }
                testId={`tooltip-${testIds.specializationLab.leverTooltip('temperature').replace(/^tooltip-/, '')}`}
                wrap
              >
                <label className="flex items-center gap-1.5">
                  <span className="font-bold">Temperature</span>
                  <input
                    type="range"
                    data-testid={testIds.specializationLab.temperatureSlider}
                    min={0}
                    max={1.5}
                    step={0.05}
                    value={temperature}
                    onChange={(e) => setTemperature(Number(e.target.value))}
                    className="w-32 cursor-pointer"
                  />
                  <span className="w-10 text-right font-mono">
                    {temperature.toFixed(2)}
                  </span>
                </label>
              </HelpTip>

              <HelpTip
                short="Specialization strength — how strongly the LoRA overrides the base model."
                long={
                  <>
                    <p>
                      How strongly the specialization (LoRA adapter)
                      overrides the base model at sampling time.
                    </p>
                    <p className="mt-1 text-gray-800">
                      Reserved for a future release — Tinker's sampler
                      doesn't currently expose a per-call adapter scale,
                      so this is fixed at 1.0.
                    </p>
                  </>
                }
                testId={`tooltip-${testIds.specializationLab.leverTooltip('strength').replace(/^tooltip-/, '')}`}
                wrap
              >
                <label className="flex items-center gap-1.5 opacity-60">
                  <span className="font-bold">Specialization strength</span>
                  <input
                    type="range"
                    data-testid={testIds.specializationLab.strengthSlider}
                    min={0}
                    max={1}
                    step={0.05}
                    value={loraStrength}
                    onChange={(e) => setLoraStrength(Number(e.target.value))}
                    disabled
                    className="w-32 cursor-not-allowed"
                  />
                  <span className="w-10 text-right font-mono">
                    {loraStrength.toFixed(2)}
                  </span>
                  <span className="italic text-gray-700 text-[10px]">(v2)</span>
                </label>
              </HelpTip>
            </div>
          </fieldset>

          {/* Outputs groupbox */}
          <fieldset
            data-testid={testIds.specializationLab.outputsGroup}
            className="border border-solid border-[#7f9db9] bg-white px-3 pt-1 pb-2 flex flex-col min-h-[260px]"
          >
            <legend className="px-1 text-[11px] font-bold text-black">
              Side-by-side outputs
            </legend>
            {/* Side-by-side columns stack vertically on mobile so each
                generation column stays readable. */}
            <section className="flex gap-2 flex-1 overflow-hidden max-md:flex-col max-md:overflow-visible max-md:gap-3">
              <Column
                title="Base model"
                testIdRoot={testIds.specializationLab.baseColumn}
                testIdOutput={testIds.specializationLab.baseOutput}
                testIdStatus={testIds.specializationLab.baseStatus}
                state={baseState}
                emptyHint={
                  loraUrl === null ? (
                    <>
                      <div className="mb-1 font-bold not-italic text-black">
                        Train a specialization to fill this column.
                      </div>
                      <div>
                        Open the{' '}
                        <button
                          type="button"
                          onClick={() => openWindow('/skills')}
                          className="text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0 italic"
                        >
                          Skill Library
                        </button>
                        , pick a skill, and click{' '}
                        <em>"Specialize the model on this skill."</em> When
                        training finishes, this column will show what the{' '}
                        <strong>base</strong> Llama-3 model produces for the
                        probe above.
                      </div>
                    </>
                  ) : (
                    <>
                      Click{' '}
                      <strong className="not-italic">↻ Run both</strong>{' '}
                      above to send the probe to the base Llama-3 model.
                      The specialized model runs in parallel on the right.
                    </>
                  )
                }
              />
              <Column
                title="Specialized model"
                testIdRoot={testIds.specializationLab.loraColumn}
                testIdOutput={testIds.specializationLab.loraOutput}
                testIdStatus={testIds.specializationLab.loraStatus}
                state={loraState}
                emptyHint={
                  loraUrl === null ? (
                    <>
                      <div className="mb-1 font-bold not-italic text-black">
                        No specialization trained yet.
                      </div>
                      <div>
                        A "specialization" is a LoRA adapter — a small set
                        of extra weights — that nudges the base model toward
                        your firm's house style for one specific Skill.
                        Open the{' '}
                        <button
                          type="button"
                          onClick={() => openWindow('/skills')}
                          className="text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0 italic"
                        >
                          Skill Library
                        </button>{' '}
                        and click <em>"Specialize the model on this skill"</em>{' '}
                        on the Skill you want to train on. It takes ~2–5
                        minutes; when it's done, this column will show how
                        the specialized model answers the same probe.
                      </div>
                    </>
                  ) : (
                    <>
                      Click{' '}
                      <strong className="not-italic">↻ Run both</strong>{' '}
                      above. This column shows what the model answers{' '}
                      <em>with</em> your specialization layered on — so you
                      can compare it to the unspecialized base model on the
                      left.
                    </>
                  )
                }
              />
            </section>
          </fieldset>

          {whatChanged && (
            <section
              data-testid={testIds.specializationLab.whatChangedCard}
              className="border border-solid border-[#7f9db9] bg-[#fffbcc] p-2 text-[11px] text-black"
            >
              <div className="font-bold mb-1">What the specialization did</div>
              <ul className="list-disc list-inside space-y-0.5">
                {whatChanged.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={testIds.specializationLab.askNipeButton}
                onClick={() => nipe.open()}
                title="Open the NIPE chat with the base vs specialized output as context — useful for asking why the outputs diverged."
                className="text-[10px] text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0 mt-1"
              >
                Ask NIPE for more detail →
              </button>
            </section>
          )}
        </main>

        <CorpusDrawer
          skillNames={skillNames}
          baseModel={registeredLora?.baseModel ?? null}
          loraRank={registeredLora?.loraRank ?? null}
          trainedAt={latestRun?.completedAt ?? null}
          onChangeSpecialization={() => openWindow('/skills')}
        />
      </div>
    </div>
  );
}
